import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { apiurl } from "../config/config";
import { useParams, useNavigate } from "react-router-dom";
import { FileSearch, AlertTriangle, Tag, PlusCircle } from "lucide-react";
import heic2any from "heic2any";
import {
  ArrowLeft,
  Save,
  UserRound,
  FileText,
  MessageSquare,
  History,
  Paperclip,
  Search,
  X,
  UploadCloud,
  Send,
} from "lucide-react";
import Layout, { Container } from "../components/layout";
import { getCookie } from "../config/webStorage";
import { useUser } from "../config/userProvider";
import { CATEGORY_MAP } from "../json/categoryMap.js";
import { DEPARTMENTS } from "../json/departments.js";
import { toast } from "react-toastify";
import AgentComplaintCard from "../components/agentComplaintCard.js";

// ManageGrievance — Full professional version
// Features:
// - Fetch grievance details
// - Edit details (category, subcategory, title, description, priority, status)
// - Assignment drawer (search employees, assign)
// - Comments UI (public/internal) with attachments
// - Attachments manager (upload + download list)
// - History timeline

const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];
const STATUS_OPTIONS = [
  "new",
  "open",
  "in_progress",
  "waiting_customer",
  "escalated",
  "cancelled",
  "reopened",
  "hold",
];
const generateSubject = (category, subcategory) => {
  if (!category) return "";

  const catLabel = CATEGORY_MAP[category]?.label;
  const subLabel = CATEGORY_MAP[category]?.subs.find(
    (s) => s.key === subcategory,
  )?.label;

  return subLabel ? `${catLabel} - ${subLabel}` : catLabel;
};
const formatHistoryText = (h) => {
  const user = h.actionBy?.userName || "System";

  switch (h.type) {
    case "department_assigned":
      return `${user} assigned ticket to ${h.to?.toUpperCase()} team`;

    case "department_responded":
      return `${user} responded from ${h.from?.toUpperCase()} team`;

    case "ESCALATED":
    case "escalated":
      return `${user} escalated ticket from ${h.from?.toUpperCase()} to ${h.to?.toUpperCase()}`;

    case "STATUS_CHANGED":
      return `${user} changed status from ${h.from} to ${h.to}`;

    case "RESOLVED":
      return `${user} resolved the ticket`;

    case "CLOSED":
      return `${user} closed the ticket`;

    default:
      return `${user} performed ${h.type}`;
  }
};

const ICONS = {
  CREATE: "🟢",
  UPDATE: "✏️",
  COMMENT: "💬",
  ATTACHMENT: "📎",
  DEPARTMENT_ACTION: "🏢",
};

// const CATEGORY_MAP = {
//   behaviour_issue: {
//     label: "Behaviour Issue",
//     subs: [
//       { key: "rude_tone", label: "Rude Tone" },
//       { key: "abusive", label: "Abusive" },
//     ],
//   },
//   delivery_issue: {
//     label: "Delivery Issue",
//     subs: [
//       { key: "delay_delivery", label: "Delay Delivery" },
//       { key: "india_post", label: "India Post" },
//       { key: "online_order", label: "Online Order" },
//       { key: "delivery_issue", label: "Delivery Issue" },
//     ],
//   },
//   product_complaint: {
//     label: "Product Complaint",
//     subs: [
//       { key: "medicine_not_effective", label: "Medicine Not Effective" },
//       { key: "product_quality_issue", label: "Product Quality Issue" },
//       { key: "quantity_issue", label: "Quantity Issue" },
//       { key: "manufacturing_issue", label: "Manufacturing Issue" },
//       { key: "wrong_product", label: "Wrong Product" },
//       { key: "missing_product", label: "Missing Product" },
//     ],
//   },
//   payment_refund_request: {
//     label: "Payment Refund Request",
//     subs: [
//       { key: "refund_not_received", label: "Refund Not Received" },
//       { key: "payment_failed", label: "Payment Failed" },
//     ],
//   },
//   need_to_talk: { label: "Need to Talk", subs: [] },
//   number_block_request: { label: "Number Block Request", subs: [] },
//   wrong_punch_or_info: {
//     label: "Wrong Punch / Wrong Medicine Info",
//     subs: [
//       { key: "wrong_punch", label: "Wrong Punch" },
//       {
//         key: "wrong_medicine_information",
//         label: "Wrong Medicine Information",
//       },
//     ],
//   },
//   technical_glitch: {
//     label: "Technical Glitch",
//     subs: [
//       {
//         key: "payment_done_but_order_missing",
//         label: "Payment Done But Order Missing",
//       },
//     ],
//   },
//   other: { label: "Other", subs: [{ key: "other", label: "Other" }] },
// };
export default function ManageTicket() {
  const token = getCookie("scspt"); // get auth token
  const { ticketObjId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ticket, seTicket] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const { userData } = useUser();

  console.log("DEPARTMENTS", DEPARTMENTS);

  // form state mirrors editable fields
  const [form, setForm] = useState({
    category: "",
    subcategory: "",
    subject: "",
    description: "",
    priority: "medium",
    status: "",
  });

  // Assignment drawer
  const [showAssignDrawer, setShowAssignDrawer] = useState(false);
  const [empQuery, setEmpQuery] = useState("");
  const [employees, setEmployees] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  // Comments
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentVisibility, setCommentVisibility] = useState("public");
  const [commentFile, setCommentFile] = useState(null);
  const [postingComment, setPostingComment] = useState(false);

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const attachmentRef = useRef(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  useEffect(() => {
    fetchDetails();
  }, [ticketObjId]);

  // Fetch grievance details once
  async function fetchDetails() {
    setLoading(true);
    try {
      const res = await axios.get(`${apiurl}/api/cst/get/${ticketObjId}`);
      const g = res.data.ticket;
      setData(g);
      seTicket(res.data.ticket);
      setForm((prev) => ({
        ...prev,
        category: g.category || "",
        subcategory: g.subcategory || "",
        subject: g.subject || "",
        description: g.description || "",
        priority: g.priority || "MEDIUM",
        status: g.status || "OPEN",
      }));

      setComments(g.comments || []);
      setAttachments(g.attachments || []);
    } catch (err) {
      console.error("fetchDetails", err);
      alert("Failed to load grievance");
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const saveChanges = async () => {
    setSaving(true);

    try {
      const payload = {
        _id: ticketObjId,

        // 🔥 ACTOR (who is making the change)
        actor: {
          userName: userData?.firstName + " " + userData.lastName, // OR logged-in user
          userEmail: userData?.email,
          userObjId: userData?._id,
        },

        // 🔥 UPDATED FIELDS
        category: form.category,
        subcategory: form.subcategory,
        title: form.title,
        description: form.description,
        priority: form.priority,
        status: form.status,
      };

      await axios.put(`${apiurl}/api/cst/update`, payload);

      await fetchDetails();
      alert("Saved successfully");
    } catch (err) {
      console.error("saveChanges", err);
      alert(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Assignment drawer helpers
  //   useEffect(() => {
  //     // simple debounce for employee search
  //     const id = setTimeout(() => {
  //       if (empQuery && showAssignDrawer) fetchEmployees(empQuery);
  //     }, 350);
  //     return () => clearTimeout(id);
  //   }, [empQuery, showAssignDrawer]);

  //   async function fetchEmployees(q) {
  //     try {
  //       const res = await axios.get(`${apiurl}/api/employees?search=${encodeURIComponent(q)}`);
  //       setEmployees(res.data?.employees || []);
  //     } catch (err) {
  //       console.error("fetchEmployees", err);
  //       setEmployees([]);
  //     }
  //   }

  // useEffect(() => {
  // // simple debounce for employee search
  // const id = setTimeout(() => {
  // if (empQuery && showAssignDrawer) fetchEmployees(empQuery);
  // }, 350);
  // return () => clearTimeout(id);
  // }, [empQuery, showAssignDrawer]);

  // async function fetchEmployees(q) {
  // try {
  // const res = await axios.get(`${apiurl}/api/user/search?search=${encodeURIComponent(q)}&limit=50`);
  // setEmployees(res.data?.users || []);
  // } catch (err) {
  // console.error("fetchEmployees", err);
  // setEmployees([]);
  // }
  // }

  //   async function assignTo(emp) {
  //     if (!emp) return;
  //     setAssigning(true);
  //     try {
  //       // payload shape depends on backend — sending assignee object
  //       await axios.post(`${apiurl}/api/grievance/assign/${ticketObjId}`, {
  //         assignee: {
  //           userName: emp.name || emp.userName || emp.fullName,
  //           userEmail: emp.email || emp.userEmail,
  //           userObjId: emp._id || emp.id,
  //           employeeCode: emp.employeeCode || emp.code || "",
  //         },
  //       });

  //       // refresh details
  //       await fetchDetails();
  //       setShowAssignDrawer(false);
  //     } catch (err) {
  //       console.error("assignTo", err);
  //       alert("Assign failed");
  //     } finally {
  //       setAssigning(false);
  //     }
  //   }

  useEffect(() => {
    const id = setTimeout(() => {
      if (empQuery && showAssignDrawer) fetchEmployees(empQuery);
    }, 350);
    return () => clearTimeout(id);
  }, [empQuery, showAssignDrawer]);

  async function fetchEmployees(q) {
    try {
      const res = await axios.get(
        `${apiurl}/api/user/search?search=${encodeURIComponent(q)}&limit=50`,
      );
      setEmployees(res.data?.users || []);
    } catch (err) {
      console.error("fetchEmployees", err);
      setEmployees([]);
    }
  }

  // ✅ Correct Assign Function — matches your backend API exactly
  async function assignTo(emp) {
    if (!emp) return;
    setAssigning(true);

    try {
      await axios.put(
        `${apiurl}/api/grievance/assignToUser`,
        {
          ticketObjId, // <- use same variable you already have
          assignee: {
            userName: emp.firstName + " " + emp.lastName || emp.name,
            userEmail: emp.userEmail || emp.email,
            userObjId: emp._id || emp.id,
            employeeCode: `SUW${emp.employeeDetails.employeeId || emp.employeeCode || ""}`,
          },
        },
        {
          headers: {
            Authorization: token, // ensure token is passed
          },
        },
      );

      await fetchDetails(); // refresh UI
      setShowAssignDrawer(false); // close drawer
    } catch (err) {
      console.error("assignTo Error:", err);
      alert(err?.response?.data?.message || "Assignment failed");
    } finally {
      setAssigning(false);
    }
  }

  // Comments
  const onCommentFile = (e) => setCommentFile(e.target.files?.[0] || null);

  // async function postComment() {
  //   if (!commentText && !commentFile) {
  //     alert("Please add a comment or attach a file");
  //     return;
  //   }

  //   if (!userData?._id) {
  //     alert("User not identified");
  //     return;
  //   }

  //   setPostingComment(true);

  //   try {
  //     const fd = new FormData();

  //     // 🔹 REQUIRED FIELDS
  //     fd.append("userObjId", userData._id);      // ✅ IMPORTANT
  //     fd.append("comment", commentText);
  //     fd.append("visibility", commentVisibility || "public");
  //     fd.append("ticketObjId", ticketObjId);

  //     // 🔹 OPTIONAL FILE
  //     if (commentFile) {
  //       fd.append("attachment", commentFile);
  //     }

  //     const res = await axios.post(
  //       `${apiurl}/api/cst/add-comment`,
  //       fd,
  //       {
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //         },
  //       }
  //     );

  //     // 🔹 Add new comment on top
  //     if (res.data?.comment) {
  //       setComments((prev) => [res.data.comment, ...prev]);
  //     }

  //     // 🔹 Reset UI
  //     setCommentText("");
  //     setCommentFile(null);
  //     if (attachmentRef.current) attachmentRef.current.value = null;

  //   } catch (err) {
  //     console.error("postComment error:", err);
  //     alert(err?.response?.data?.message || "Comment failed");
  //   } finally {
  //     setPostingComment(false);
  //   }
  // }

  // Attachments manager

  async function postComment() {
    // ✅ Validate input
    if (!commentText?.trim() && !commentFile) {
      alert("Please add a comment or attach a file");
      return;
    }

    if (!userData?._id) {
      alert("User not identified");
      return;
    }

    setPostingComment(true);

    try {
      const fd = new FormData();

      // 🔹 REQUIRED FIELDS
      fd.append("userObjId", userData._id);
      fd.append("ticketObjId", ticketObjId);
      fd.append("comment", commentText.trim()); // ✅ TRIM IMPORTANT
      fd.append("visibility", commentVisibility || "public");

      // 🔹 OPTIONAL FILE
      if (commentFile) {
        fd.append("attachment", commentFile);
      }

      // const res = await axios.post(
      //   `${apiurl}/api/cst/add-comment`,
      //   fd
      //   // ❌ DO NOT SET Content-Type MANUALLY
      // );

      const res = await axios.post(`${apiurl}/api/cst/add-comment`, {
        userObjId: userData._id,
        ticketObjId,
        comment: commentText,
        visibility: commentVisibility,
      });

      // 🔹 Add new comment on top (instant UI update)
      if (res.data?.comment) {
        setComments((prev) => [res.data.comment, ...prev]);
      }

      // 🔹 Reset UI
      setCommentText("");
      setCommentFile(null);
      if (attachmentRef.current) {
        attachmentRef.current.value = null;
      }
    } catch (err) {
      console.error("postComment error:", err);
      alert(err?.response?.data?.message || "Comment failed");
    } finally {
      setPostingComment(false);
    }
  }
  const onAttachmentFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    console.log("Selected file:", file); // 👈 MUST LOG

    try {
      setUploadingAttachment(true);

      const mediaRes = await uploadToMedia(file);

      console.log("mediaRes", mediaRes);

      await saveAttachmentToTicket(mediaRes.url);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      await fetchDetails();
      setUploadingAttachment(false);
      e.target.value = "";
    }
  };

  const [attachmentDesc, setAttachmentDesc] = useState("");

  async function uploadToMedia(file) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", `cst-${data?.ticketId}`);

    const res = await axios.post(`${apiurl}/api/media/upload-attachment`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    // ✅ return ONLY what is needed
    return {
      url: res.data.fileUrl,
    };
  }

  // async function saveAttachmentToTicket(fileUrl) {
  //   return axios.post(`${apiurl}/api/cst/add-attachment`, {
  //     ticketObjId,
  //     attachment: {
  //       url: fileUrl,
  //       description: attachmentDesc || "No comment",
  //     },
  //     actor: {
  //       userObjId: userData._id,
  //       userName: `${userData.firstName} ${userData.lastName}`,
  //       userEmail: userData.email,
  //     },
  //   });
  // }

  async function saveAttachmentToTicket(fileUrl) {
    return axios.post(
      `${apiurl}/api/cst/add-attachment`,
      {
        ticketObjId,
        attachment: {
          url: fileUrl,
          description: attachmentDesc || "No comment",
        },
        actor: {
          userObjId: userData._id,
          userName: `${userData.firstName} ${userData.lastName}`,
          userEmail: userData.email,
        },
      },
      {
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      },
    );
  }


  async function removeAttachmentFromTicket(attachmentObjId) {
  return axios.post(
    `${apiurl}/api/cst/remove-attachment`,
    {
      ticketObjId,
      attachmentObjId, // 🔥 must send ID (not index)
      actor: {
        userObjId: userData._id,
        userName: `${userData.firstName} ${userData.lastName}`,
        userEmail: userData.email,
      },
    },
    {
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }
  );
}


const removeAttachment = async (attachmentId) => {
  try {
    await removeAttachmentFromTicket(attachmentId);

    // 🔥 update UI instantly
    setAttachments((prev) =>
      prev.filter((att) => att._id !== attachmentId)
    );

  } catch (error) {
    console.error("Remove attachment error:", error);
  }
};


  

  console.log("ticket", ticket);

  const handleResolve = async (ticketId) => {
    try {
      await axios.post(`${apiurl}/api/cst/resolved`, {
        ticketObjId: ticketId,
        userObjId: userData._id,
      });

      // Refresh tickets after update
      fetchDetails();
    } catch (error) {
      console.error("Resolve error:", error);
    }
  };

  const handleClose = async (ticketId) => {
    try {
      await axios.post(`${apiurl}/api/cst/closed`, {
        ticketObjId: ticketId,
        userObjId: userData._id,
      });

      // Refresh tickets after update
      fetchDetails();
    } catch (error) {
      console.error("Close error:", error);
    }
  };




  const [showUploadModal, setShowUploadModal] = useState(false);
const [bulkFiles, setBulkFiles] = useState([]);
const [uploadProgress, setUploadProgress] = useState({});
const [uploadingBulk, setUploadingBulk] = useState(false);

const handleBulkSelect = (files) => {
  const fileArray = Array.from(files);

  const mapped = fileArray.map((file) => ({
    file,
    id: `${file.name}-${Date.now()}`,
  }));

  setBulkFiles((prev) => [...prev, ...mapped]);
};


const uploadBulkFiles = async () => {
  if (!bulkFiles.length) return;

  setUploadingBulk(true);
    let allSuccess = true; // 🔥 track status

  try {
    for (let i = 0; i < bulkFiles.length; i++) {
      const item = bulkFiles[i];

      try {
        // 🔥 progress start
        setUploadProgress((prev) => ({
          ...prev,
          [item.id]: 10,
        }));

        const mediaRes = await uploadToMedia(item.file);

        setUploadProgress((prev) => ({
          ...prev,
          [item.id]: 70,
        }));

        await saveAttachmentToTicket(mediaRes.url);

        // ✅ done
        setUploadProgress((prev) => ({
          ...prev,
          [item.id]: 100,
        }));

      } catch (err) {
        console.error("File upload failed:", item.file.name);
        setUploadProgress((prev) => ({
          ...prev,
          [item.id]: -1, // ❌ failed
        }));
      }
    }

    await fetchDetails();
    toast.success("Bulk upload completed");

        // ✅ AUTO CLOSE ONLY IF ALL SUCCESS
    if (allSuccess) {
      toast.success("All files uploaded successfully");

      // small delay for UX (optional but better)
      setTimeout(() => {
        setShowUploadModal(false);
        setBulkFiles([]);
        setUploadProgress({});
      }, 800);
    } else {
      toast.warning("Some files failed. Please retry.");
    }


  } catch (err) {
    console.error(err);
  } finally {
    setUploadingBulk(false);
  }
};
  

  if (loading)
    return (
      <Layout>
        <Container>
          <div className="p-8 text-center">Loading...</div>
        </Container>
      </Layout>
    );

  return (
    <Layout>
      <Container overflow={"overflow-auto"}>
        <div className="mx-auto m-2 p-4 bg-slate-50">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded hover:bg-gray-100"
              >
                <ArrowLeft size={18} />
              </button>
              <div className="space-y-1">
                {/* SUBJECT */}
                <h1 className="text-2xl font-semibold text-gray-900 leading-snug">
                  {data.subject || "—"}
                </h1>

                {/* META ROW */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  <span className="font-medium text-gray-700">
                    Ticket #{data.ticketId}
                  </span>
                  <span>•</span>
                  <span>
                    Created {new Date(data.createdAt).toLocaleString()}
                  </span>
                  <span>•</span>
                  <span className="capitalize">
                    {data.channel?.replace("-", " ")}
                  </span>
                </div>
              </div>
            </div>
<div className="flex items-center justify-end">
            <div className="flex items-center gap-1 mx-4">
              <button
                onClick={saveChanges}
                disabled={saving}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded"
              >
                <Save size={16} /> {saving ? "Saving..." : "Save"}
              </button>
            </div>

  {ticket.status === "closed" ? (
  <div style={{ color: "green", fontWeight: "600" }}>
    ✔ Closed
    {ticket.closedBy?.userName && (
      <div style={{ fontSize: "12px", color: "#666" }}>
        Closed by: {ticket.closedBy.userName}
      </div>
    )}
  </div>
) : (
  <>
    {ticket.status === "resolved" ? (
      <div style={{ color: "green", fontWeight: "600" }}>
        ✔ Resolved
        {ticket.resolvedBy?.userName && (
          <div style={{ fontSize: "12px", color: "#666" }}>
            Resolved by: {ticket.resolvedBy.userName}
          </div>
        )}
      </div>
    ) : (
      <>
      <button
        onClick={() => handleResolve(ticket._id)}
        style={{
          background: "#28a745",
          color: "white",
          border: "none",
          padding: "6px 12px",
          borderRadius: "4px",
          cursor: "pointer",
          marginRight: "8px",
        }}
      >
        Resolve
      </button>

          <button
      onClick={() => handleClose(ticket._id)}
      style={{
        background: "#dc3545",
        color: "white",
        border: "none",
        padding: "6px 12px",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      Close
    </button>
    </>
    )}
  </>
)}
          </div>
</div>

          {/* Ticket top card: requester, assignee, status/priority */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            {/* Requester */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-lg">
                {data.createdBy?.userName?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div>
                <div className="font-medium">{data.createdBy?.userName}</div>
                <div className="text-xs text-gray-500">
                  {data.createdBy?.employeeCode} • {data.createdBy?.userEmail}
                </div>
              </div>
            </div>

            {/* Assignee */}
            <div className="flex items-center gap-3">
              <div className="text-xs text-gray-400">Assigned To</div>
              {data.assignee ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-semibold text-sm">
                    {data.assignee.userName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium">{data.assignee.userName}</div>
                    <div className="text-xs text-gray-500">
                      {data.assignee.employeeCode}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAssignDrawer(true)}
                  className="text-sm px-3 py-1 border rounded hover:bg-gray-50"
                >
                  + Assign
                </button>
              )}
            </div>

            {/* Status & Priority */}
            <div className="flex flex-col items-end gap-2">
              <div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${form.status === "OPEN" ? "bg-blue-100 text-blue-700" : form.status === "IN_PROGRESS" ? "bg-yellow-100 text-yellow-700" : form.status === "RESOLVED" ? "bg-green-100 text-green-700" : form.status === "CLOSED" ? "bg-gray-100 text-gray-700" : "bg-indigo-100 text-indigo-700"}`}
                >
                  {form.status}
                </span>
              </div>
              <div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${form.priority === "LOW" ? "bg-gray-100 text-gray-700" : form.priority === "MEDIUM" ? "bg-yellow-100 text-yellow-700" : form.priority === "HIGH" ? "bg-orange-100 text-orange-700" : "bg-red-100 text-red-700"}`}
                >
                  {form.priority}
                </span>
              </div>
            </div>
          </div>

          {/* Main area */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Details & Edit */}
            <div className="lg:col-span-2 space-y-4">
              {/* Tabs */}
              <div className="flex gap-4 border-b pb-2">
                <button
                  onClick={() => setActiveTab("details")}
                  className={`flex items-center gap-2 pb-2 ${activeTab === "details" ? "border-b-2 border-indigo-600 font-semibold" : "text-gray-500"}`}
                >
                  <FileText size={16} /> Details
                </button>
                <button
                  onClick={() => setActiveTab("comments")}
                  className={`flex items-center gap-2 pb-2 ${activeTab === "comments" ? "border-b-2 border-indigo-600 font-semibold" : "text-gray-500"}`}
                >
                  <MessageSquare size={16} /> Comments
                </button>
                <button
                  onClick={() => setActiveTab("history")}
                  className={`flex items-center gap-2 pb-2 ${activeTab === "history" ? "border-b-2 border-indigo-600 font-semibold" : "text-gray-500"}`}
                >
                  <History size={16} /> History
                </button>
                <button
                  onClick={() => setActiveTab("attachments")}
                  className={`flex items-center gap-2 pb-2 ${activeTab === "attachments" ? "border-b-2 border-indigo-600 font-semibold" : "text-gray-500"}`}
                >
                  <Paperclip size={16} /> Attachments
                </button>
                <button
                  onClick={() => setActiveTab("departments")}
                  className={`flex items-center gap-2 pb-2 ${
                    activeTab === "departments"
                      ? "border-b-2 border-indigo-600 font-semibold"
                      : "text-gray-500"
                  }`}
                >
                  🏢 Departments
                </button>
              </div>

              {/* Details */}
              {activeTab === "details" && (
                <div className="bg-white p-4 rounded shadow-sm">
                  {/* Customer & Order Details */}
                  <div className="bg-white rounded-lg shadow-sm border p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* CUSTOMER */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-600 mb-3">
                        Customer Details
                      </h3>

                      <div className="text-sm space-y-1">
                        <div>
                          <span className="text-gray-500">Name:</span>{" "}
                          <span className="font-medium">
                            {data.customer?.name || "—"}
                          </span>
                        </div>

                        {/* <div>
        <span className="text-gray-500">Phone:</span>{" "}
        <span className="font-medium">
          {data.customer?.phone || "—"}
        </span>
      </div> */}

                        <div>
                          <span className="text-gray-500">Email:</span>{" "}
                          <span className="font-medium">
                            {data.customer?.email || "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* ORDER */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-600 mb-3">
                        Order Details
                      </h3>

                      {data.order ? (
                        <div className="text-sm space-y-1">
                          <div>
                            <span className="text-gray-500">Order ID:</span>{" "}
                            <span className="font-medium">
                              {data.order.orderId}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">Amount:</span>{" "}
                            <span className="font-medium">
                              ₹{data.order.amount}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">
                              Payment Status:
                            </span>{" "}
                            <span
                              className={`font-medium ${
                                data.order.paymentStatus === "pending"
                                  ? "text-yellow-600"
                                  : "text-green-600"
                              }`}
                            >
                              {data.order.paymentStatus || "—"}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">Order Date:</span>{" "}
                            <span className="font-medium">
                              {data.order.orderDate
                                ? new Date(
                                    data.order.orderDate,
                                  ).toLocaleString()
                                : "—"}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">
                              Delivery Status:
                            </span>{" "}
                            <span className="font-medium">
                              {data.order.deliveryStatus || "—"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">
                          No order linked
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Category</label>
                      {/* <input name="category" value={form.category} onChange={handleChange} className="w-full p-2 border rounded mt-1" /> */}
                      <select
                        value={form.category}
                        onChange={(e) => {
                          const category = e.target.value;
                          setForm((f) => ({
                            ...f,
                            category,
                            subcategory: "",
                            title: generateSubject(category, ""),
                          }));
                        }}
                        className="w-full p-2 border rounded mt-1"
                      >
                        <option value="">Select Complaint Type</option>
                        {Object.entries(CATEGORY_MAP).map(([key, obj]) => (
                          <option key={key} value={key}>
                            {obj.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm text-gray-600">
                        Subcategory
                      </label>
                      {/* <input name="subcategory" value={form.subcategory} onChange={handleChange} className="w-full p-2 border rounded mt-1" /> */}

                      <select
                        value={form.subcategory}
                        onChange={(e) => {
                          const subcategory = e.target.value;
                          setForm((f) => ({
                            ...f,
                            subcategory,
                            title: generateSubject(f.category, subcategory),
                          }));
                        }}
                        disabled={
                          !form.category ||
                          CATEGORY_MAP[form.category]?.subs.length === 0
                        }
                        className="w-full p-2 border rounded mt-1"
                      >
                        <option value="">
                          {CATEGORY_MAP[form.category]?.subs.length
                            ? "Select Sub Complaint"
                            : "No Sub Complaint"}
                        </option>

                        {CATEGORY_MAP[form.category]?.subs.map((sub) => (
                          <option key={sub.key} value={sub.key}>
                            {sub.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-600">Subject</label>
                      {/* <input name="title" value={form.title} onChange={handleChange} className="w-full p-2 border rounded mt-1" /> */}
                      <input
                        value={form.subject}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, title: e.target.value }))
                        }
                        className="w-full p-2 border rounded mt-1 font-medium"
                        placeholder="Subject (auto-generated)"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-600">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={6}
                        className="w-full p-2 border rounded mt-1"
                      />
                    </div>
                    {["admin", "master"].includes(userData?.userType) ? (
                      <>
                        <div>
                          <label className="text-sm text-gray-600">
                            Priority
                          </label>
                          <select
                            name="priority"
                            value={form.priority}
                            onChange={handleChange}
                            className="w-full p-2 border rounded mt-1"
                          >
                            {PRIORITY_OPTIONS.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="text-sm text-gray-600">
                            Status
                          </label>
                          <select
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                            className="w-full p-2 border rounded mt-1"
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Comments */}
              {activeTab === "comments" && (
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Write a comment..."
                        className="flex-1 p-2 border rounded"
                        rows={3}
                      />
                      <div className="flex flex-col gap-2 items-end">
                        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                          <UploadCloud size={16} />
                          <input
                            ref={attachmentRef}
                            type="file"
                            className="hidden"
                            onChange={onCommentFile}
                          />
                        </label>
                        <select
                          value={commentVisibility}
                          onChange={(e) => setCommentVisibility(e.target.value)}
                          className="p-2 border rounded text-sm"
                        >
                          <option value="public">Public</option>
                          <option value="internal">Internal</option>
                        </select>
                        <button
                          onClick={postComment}
                          disabled={postingComment}
                          className="bg-indigo-600 text-white px-3 py-2 rounded flex items-center gap-2"
                        >
                          <Send size={14} />{" "}
                          {postingComment ? "Posting..." : "Post"}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2 mt-2">
                      {comments.length === 0 && (
                        <div className="text-sm text-gray-500">
                          No comments yet.
                        </div>
                      )}
                      {comments.map((c, i) => (
                        <div key={i} className="p-3 border rounded">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">
                                {c.commentedBy?.userName ||
                                  c.commentedBy?.userEmail ||
                                  "User"}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(c.createdAt).toLocaleString()} •{" "}
                                {c.visibility}
                              </div>
                            </div>
                            {c.attachment && (
                              <a
                                href={c.attachment.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600"
                              >
                                Download
                              </a>
                            )}
                          </div>
                          <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                            {c.comment}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* History */}
              {/* {activeTab === "history" && (
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="space-y-3">
                    {data.history?.length === 0 && (
                      <div className="text-sm text-gray-500">
                        No history available.
                      </div>
                    )}
                    {data.history?.map((h, i) => (
                      <div
                        key={i}
                        className="p-3 border rounded flex justify-between items-start bg-gray-50"
                      >
                        <div className="flex gap-3">
                          <div className="mt-1 text-indigo-600">🕒</div>

                          <div>
                            <div className="text-sm font-medium text-gray-800">
                              {formatHistoryText(h)}
                            </div>

                            {h.note && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {h.note}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(h.createdAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )} */}

              {/* {activeTab === "history" && (
  <div className="bg-slate-50 p-4 rounded">
    {data.history?.length === 0 ? (
      <div className="text-sm text-gray-500">
        No activity recorded yet.
      </div>
    ) : (
      <div className="space-y-2">
        {data.history.map((h, i) => (
          <HistoryItem key={i} h={h} />
        ))}
      </div>
    )}
  </div>
)} */}

              {activeTab === "history" && (
                <div className="bg-slate-50 p-4 rounded-xl">
                  {data.history?.length === 0 ? (
                    <div className="text-sm text-gray-500">No activity yet</div>
                  ) : (
                    <div className="space-y-2">
                      {data.history.map((h) => (
                        <HistoryItem
                          key={h._id}
                          h={h}
                          onViewImage={setPreviewImg}
                        />
                      ))}

                      {/* Global attachments preview (ticket level) */}
                      {data.attachments?.length > 0 && (
                        <div className="mt-6">
                          <h4 className="text-sm font-semibold mb-2">
                            Attachments
                          </h4>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {data.attachments.map((a) =>
                              isImage(a.url) ? (
                                <div
                                  key={a._id}
                                  className="relative group cursor-pointer"
                                  onClick={() => setPreviewImg(a.url)}
                                >
                                  <img
                                    src={a.url}
                                    className="h-28 w-full object-cover rounded border"
                                  />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-sm">
                                    View
                                  </div>
                                </div>
                              ) : (
                                <a
                                  key={a._id}
                                  href={a.url}
                                  target="_blank"
                                  className="border p-2 rounded text-sm"
                                >
                                  📄 Download
                                </a>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <ImagePreviewModal
                    src={previewImg}
                    onClose={() => setPreviewImg(null)}
                  />
                </div>
              )}

              {/* Attachments */}
              {activeTab === "attachments" && (
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-gray-600">Attachments</div>

                    {/* <input
                      type="text"
                      placeholder="Attachment description (optional)"
                      value={attachmentDesc}
                      onChange={(e) => setAttachmentDesc(e.target.value)}
                      className="w-full p-2 border rounded"
                    /> */}

                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        className="hidden"
                        id="attach-input"
                        onChange={onAttachmentFile}
                      />

                      <label
                        htmlFor="attach-input"
                        className="flex items-center gap-2 cursor-pointer border px-3 py-2 rounded text-sm hover:bg-gray-50"
                      >
                        <UploadCloud size={16} /> Upload
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {attachments.length === 0 && (
                      <div className="text-sm text-gray-500">
                        No attachments
                      </div>
                    )}
                    {attachments.map((a, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 border rounded"
                      >
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-blue-600"
                        >
                          {a.filename || a.fileName || "Attachment"}
                        </a>
                        <div className="flex items-center gap-2">
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm px-2 py-1 border rounded"
                          >
                            View
                          </a>
                          <button
                            onClick={() => removeAttachment(a._id)}
                            className="text-sm px-2 py-1 border rounded text-red-600"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                                  <button
  onClick={() => setShowUploadModal(true)}
  className="mt-4 border px-3 py-2 rounded w-full flex items-center justify-center gap-2 text-sm hover:bg-gray-50"
>
  + Upload Bulk Files
</button>

{/* 
<div
  onDragOver={(e) => e.preventDefault()}
  onDrop={(e) => {
    e.preventDefault();
    handleBulkSelect(e.dataTransfer.files);
  }}
  className="border-2 border-dashed p-4 rounded mb-3 text-center"
>
  Drag & Drop files here
</div> */}
                </div>
              )}

              {activeTab === "departments" && (
                <div className="bg-white p-4 rounded shadow-sm space-y-4">
                  {/* Current Team Highlight */}
                  <div className="flex items-center justify-between p-3 rounded bg-indigo-50 border border-indigo-200">
                    <div className="text-sm">
                      <div className="text-gray-500">Current Team</div>
                      <div className="font-semibold capitalize text-indigo-700">
                        {data.team || "Not Assigned"}
                      </div>
                    </div>

                    <span className="px-3 py-1 text-xs rounded-full bg-indigo-600 text-white">
                      ACTIVE
                    </span>
                  </div>

                  {/* Timeline */}
                  <div className="space-y-4">
                    {data.departmentActions?.map((d, idx) => {
                      const isActive =
                        data.team === d.department && d.status !== "responded";

                      return (
                        <div
                          key={idx}
                          className={`relative border rounded-lg p-4 transition ${
                            isActive
                              ? "border-indigo-400 bg-indigo-50"
                              : "bg-gray-50"
                          }`}
                        >
                          {/* Header */}
                          <div className="flex justify-between items-center">
                            <div className="font-medium capitalize text-gray-800">
                              {d.department}
                            </div>

                            <span
                              className={`text-xs px-3 py-1 rounded-full ${
                                d.status === "responded"
                                  ? "bg-green-100 text-green-700"
                                  : isActive
                                    ? "bg-indigo-100 text-indigo-700"
                                    : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              {d.status.replace("_", " ").toUpperCase()}
                            </span>
                          </div>

                          {/* Assigned info */}
                          <div className="text-xs text-gray-500 mt-1">
                            Assigned by{" "}
                            <span className="font-medium">
                              {d.assignedBy?.userName || "System"}
                            </span>{" "}
                            • {new Date(d.assignedAt).toLocaleString()}
                          </div>

                          {/* Response */}
                          {d.response && (
                            <div className="mt-3 bg-white border rounded p-3">
                              <div className="text-xs text-gray-500 mb-1">
                                Response by{" "}
                                <span className="font-medium">
                                  {d.respondedBy?.userName}
                                </span>{" "}
                                • {new Date(d.respondedAt).toLocaleString()}
                              </div>
                              <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                {d.response}
                              </div>
                            </div>
                          )}

                          {/* Inline Respond Box (ONLY ACTIVE TEAM) */}
                          {isActive && (
                            <DepartmentRespondBox
                              department={d.department}
                              ticketObjId={ticketObjId}
                              onDone={fetchDetails}
                              token={token}
                            />
                          )}
                        </div>
                      );
                    })}

                    {data.departmentActions?.length === 0 && (
                      <div className="text-sm text-gray-500">
                        No department activity yet.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right column: quick info + actions */}
            <div className="space-y-4">
              {["admin", "master"].includes(userData?.userType) ? (
                <div className="bg-white p-4 rounded shadow-sm space-y-3">
                  <h3 className="text-sm font-semibold text-gray-600">
                    Escalate / Assign Department
                  </h3>

                  <div className="text-xs text-gray-500">
                    This will move ownership to another department
                  </div>

                  <select
                    className="w-full p-2 border rounded text-sm"
                    defaultValue=""
                    onChange={async (e) => {
                      const department = e.target.value;
                      if (!department) return;

                      await axios.post(
                        `${apiurl}/api/cst/assign-department`,
                        {
                          ticketObjId,
                          department,
                          responseDueAt: new Date(
                            Date.now() + 24 * 60 * 60 * 1000,
                          ),
                        },
                        { headers: { Authorization: token } },
                      );

                      await fetchDetails();
                    }}
                  >
                    <option value="">Select Department</option>
                    {DEPARTMENTS &&
                      DEPARTMENTS.map((d) => (
                        <option key={d} value={d}>
                          {d.toUpperCase()}
                        </option>
                      ))}
                  </select>
                </div>
              ) : null}
              <div className="bg-white p-4 rounded shadow-sm">
                <div className="text-sm text-gray-500">Requester</div>
                <div className="mt-2">
                  <div className="font-medium">{data.requester?.userName}</div>
                  <div className="text-xs text-gray-500">
                    {data.requester?.employeeCode} • {data.requester?.userEmail}
                  </div>
                </div>
              </div>

              {data.resolver && (
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="text-sm text-gray-500">Resolver</div>
                  <div className="mt-2">
                    <div className="font-medium">{data.resolver?.userName}</div>
                    <div className="text-xs text-gray-500">
                      {data.resolver?.employeeCode} • {data.resolver?.userEmail}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white p-4 rounded shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-sm text-gray-500">SLA</div>
                </div>
                <div className="text-sm">
                  <div>
                    Created:{" "}
                    <span className="text-gray-600">
                      {new Date(data.sla?.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    Due:{" "}
                    <span className="text-gray-600">
                      {data.sla?.dueAt
                        ? new Date(data.sla.dueAt).toLocaleString()
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded shadow-sm">
                <div className="text-sm text-gray-500">Meta</div>
                {/* <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                  {JSON.stringify(data.meta || {}, null, 2)}
                </div> */}
              </div>

              <ACPTAnalysisCard
                ticket={ticket}
                ticketObjId={data._id}
                token={token}
                user={userData}
                onSuccess={() => {
                  fetchDetails(); // re-fetch ticket after ACPT update
                }}
              />

              {/* {userData?.roll === "quality"?
              <ACPTAnalysisCard
                ticket={ticket}
                ticketObjId={data._id}
                token={token}
                user={userData}
                onSuccess={() => {
                  fetchDetails(); // re-fetch ticket after ACPT update
                }}
              />
              :null} */}

              {["admin", "master"].includes(userData?.userType) ? (
                <AgentComplaintCard token={token} ticket={ticket}  onDone={fetchDetails}/>
              ) : null}


              <ComplaintCard ticket={ticket}/>
            </div>
          </div>

          {/* Assign Drawer */}
          {showAssignDrawer && (
            <div className="fixed inset-0 z-50 flex">
              {/* <div className="absolute inset-0 bg-black opacity-30" onClick={() => setShowAssignDrawer(false)} /> */}
              <div className="ml-auto w-full max-w-md bg-white h-full shadow-xl p-4 overflow-auto">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-semibold">Assign User</div>
                  <button
                    onClick={() => setShowAssignDrawer(false)}
                    className="p-2 rounded hover:bg-gray-100"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="mb-3">
                  <div className="flex items-center gap-2">
                    <input
                      value={empQuery}
                      onChange={(e) => setEmpQuery(e.target.value)}
                      placeholder="Search employee by name, code or email"
                      className="flex-1 p-2 border rounded"
                    />
                    <button
                      onClick={() => fetchEmployees(empQuery)}
                      className="px-3 py-2 bg-indigo-600 text-white rounded"
                    >
                      Search
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {employees.length === 0 && (
                    <div className="text-sm text-gray-500">No results</div>
                  )}
                  {employees.map((emp) => (
                    <div
                      key={emp._id || emp.id}
                      className="p-2 border rounded flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold">
                          {emp.name?.charAt(0)?.toUpperCase() ||
                            emp.userName?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">
                            {emp.name || emp.userName || emp.fullName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {emp.employeeCode || emp.code || ""} •{" "}
                            {emp.email || emp.userEmail}
                          </div>
                        </div>
                      </div>
                      <div>
                        <button
                          onClick={() => assignTo(emp)}
                          disabled={assigning}
                          className="px-3 py-1 bg-green-600 text-white rounded"
                        >
                          Assign
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>



{showUploadModal && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg w-[500px]">

      <h3 className="text-lg font-semibold mb-4">
        Bulk Upload Attachments
      </h3>

      {/* 🔥 Drag & Drop */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleBulkSelect(e.dataTransfer.files);
        }}
        className="border-2 border-dashed p-4 rounded mb-3 text-center"
      >
        Drag & Drop files here
      </div>

      {/* 🔥 File Input */}
      <input
        type="file"
        multiple
        onChange={(e) => handleBulkSelect(e.target.files)}
        className="mb-3"
      />

      {/* 🔥 File List + Progress */}
      <div className="max-h-40 overflow-auto space-y-2 mb-3">
        {bulkFiles.map((item) => (
          <div key={item.id} className="text-sm border p-2 rounded">
            <div className="flex justify-between">
              <span>{item.file.name}</span>

              {uploadProgress[item.id] === 100 && (
                <span className="text-green-600">Done</span>
              )}
              {uploadProgress[item.id] === -1 && (
                <span className="text-red-600">Failed</span>
              )}
            </div>

            {/* Progress Bar */}
            {uploadProgress[item.id] >= 0 &&
              uploadProgress[item.id] < 100 && (
                <div className="h-1 bg-gray-200 mt-1">
                  <div
                    className="h-1 bg-indigo-600"
                    style={{
                      width: `${uploadProgress[item.id] || 0}%`,
                    }}
                  />
                </div>
              )}
          </div>
        ))}
      </div>

      {/* 🔥 Actions */}
      <div className="flex justify-between">
        <button
          onClick={() => {
            setBulkFiles([]);
            setUploadProgress({});
          }}
          className="px-3 py-1 border rounded"
        >
          Clear
        </button>

        <div className="flex gap-2">
          <button
            onClick={() => setShowUploadModal(false)}
            className="px-3 py-1 border rounded"
          >
            Cancel
          </button>

          <button
            onClick={uploadBulkFiles}
            disabled={uploadingBulk}
            className="px-3 py-1 bg-indigo-600 text-white rounded"
          >
            {uploadingBulk ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      </Container>
    </Layout>
  );
}
function DepartmentRespondBox({ department, ticketObjId, onDone, token }) {
  const [response, setResponse] = useState("");
  const [sending, setSending] = useState(false);

  async function submit() {
    if (!response.trim()) return;

    setSending(true);
    try {
      await axios.post(
        `${apiurl}/api/cst/department-respond`,
        { department, response, ticketObjId },
        { headers: { Authorization: token } },
      );

      setResponse("");
      await onDone();
    } catch (err) {
      alert("Response failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mt-4 border-t pt-3">
      <textarea
        rows={3}
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        placeholder={`Write response from ${department} team...`}
        className="w-full p-2 border rounded text-sm"
      />

      <div className="flex justify-end mt-2">
        <button
          onClick={submit}
          disabled={sending}
          className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded"
        >
          {sending ? "Submitting..." : "Submit Response"}
        </button>
      </div>
    </div>
  );
}

const isImage = (url = "") => /\.(jpg|jpeg|png|webp|gif)$/i.test(url);

const HistoryIcon = ({ type }) => {
  switch (type) {
    case "CREATE":
      return "🆕";
    case "WHATSAPP_SENT":
      return "💬";
    case "ATTACHMENT_UPLOADED":
      return "📎";
    case "DEPARTMENT_ASSIGNED":
      return "🏢";
    case "DEPARTMENT_RESPONSE":
      return "✅";
    case "COMMENT_ADDED":
      return "🗨️";
    default:
      return "🕒";
  }
};
function ImagePreviewModal({ src, onClose }) {
  if (!src) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
      <div className="relative max-w-4xl w-full p-4">
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 bg-white rounded-full px-3 py-1 text-sm shadow"
        >
          ✕
        </button>

        <img
          src={src}
          alt="preview"
          className="w-full max-h-[80vh] object-contain rounded"
        />
      </div>
    </div>
  );
}

function HistoryItem({ h, onViewImage }) {
  return (
    <div className="flex gap-4 relative">
      {/* Timeline */}
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm">
          <HistoryIcon type={h.type} />
        </div>
        <div className="flex-1 w-px bg-gray-200 mt-1" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          {/* Header */}
          <div className="flex justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-800">
                {h.actionBy?.userName || "System"}
              </div>
              <div className="text-xs text-gray-500">
                {new Date(h.createdAt).toLocaleString()}
              </div>
            </div>

            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
              {h.type.replaceAll("_", " ")}
            </span>
          </div>

          {/* Note / Message */}
          {h.note && <div className="mt-2 text-sm text-gray-700">{h.note}</div>}

          {/* Status / Department */}
          {(h.from || h.to) && (
            <div className="mt-2 text-xs text-gray-600">
              {h.from && (
                <span>
                  From: <b>{h.from}</b>
                </span>
              )}
              {h.from && h.to && " → "}
              {h.to && (
                <span>
                  To: <b>{h.to}</b>
                </span>
              )}
            </div>
          )}

          {/* COMMENT preview */}
          {h.type === "COMMENT_ADDED" && h.meta?.preview && (
            <div className="mt-2 text-sm bg-gray-50 p-2 rounded">
              “{h.meta.preview}”
            </div>
          )}

          {/* Attachments (from main attachments array OR history) */}
          {h.type === "ATTACHMENT_UPLOADED" && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
              {/* Match ticket.attachments by time */}
              {/* You can also directly attach URLs in history later */}
              {/** thumbnails handled outside */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// const CATEGORY_OPTIONS = [
//   "incomplete-or-incorrect-information",
//   "fake-order-or-fake-commitment",
//   "miss-punch-or-wrong-product-punch",
//   "refund-or-return-policy-error",
//   "price-manipulation",
//   "other",
// ];



const ROOT_CAUSE_OPTIONS = ["agent", "customer", "process", "technical"];

function ACPTAnalysisCard({ ticket, ticketObjId, token, user, onSuccess }) {
  const [form, setForm] = useState({
    primaryRootCause: "",
    category: "",
    rootCauseDescription: "",
  });

  const [loading, setLoading] = useState(false);
  const isACPTAdded = Boolean(ticket?.acptAnalysis?.primaryRootCause);

  console.log("isACPTAdded", ticket);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.primaryRootCause) {
      alert("Primary Root Cause is required");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ticketObjId,
        acptAnalysis: {
          primaryRootCause: form.primaryRootCause,
          category: form.category || null,
          rootCauseDescription: form.rootCauseDescription || "",
        },
        actor: {
          userObjId: user._id,
          userName: user.name,
          userEmail: user.email,
        },
      };

      const res = await axios.post(`${apiurl}/api/cst/add-acpt`, payload, {
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data.success) {
        alert("ACPT added successfully");

        setForm({
          primaryRootCause: "",
          category: "",
          rootCauseDescription: "",
        });

        onSuccess?.(); // 🔥 refetch ticket
      }
    } catch (error) {
      console.error("Add ACPT error:", error);
      alert(error?.response?.data?.message || "Failed to add ACPT analysis");
    } finally {
      setLoading(false);
    }
  };


  console.log("userData",user);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
      <div></div>
      {/* Header */}
      <div className="flex items-center gap-2 text-gray-800">
        <FileSearch className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold">ACPT Analysis</h3>
      </div>

      {isACPTAdded && (
        <div className="space-y-3 text-sm border rounded-md p-2">
          {isACPTAdded && (
            <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
              Added
            </span>
          )}
          <Detail
            label="Primary Root Cause"
            value={ticket?.acptAnalysis?.primaryRootCause}
          />
          <Detail
            label="Category"
            value={ticket?.acptAnalysis?.category || "—"}
          />
          <Detail
            label="Description"
            value={ticket?.acptAnalysis?.rootCauseDescription || "Not provided"}
          />
          <Detail
            label="Added By"
            value={ticket?.acptAnalysis?.actionBy?.userName || "Unknown"}
          />
          <Detail
            label="Added At"
            value={new Date(ticket?.acptAnalysis?.uploadedAt).toLocaleString()}
          />
        </div>
      )}



                    {user?.role === "quality"|| user.userType ==="admin" || user.userType ==="master"?
                    <>



      {/* Primary Root Cause */}
      <div>
        <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          Primary Root Cause <span className="text-red-500">*</span>
        </label>
        <select
          name="primaryRootCause"
          value={form.primaryRootCause}
          onChange={handleChange}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select root cause</option>
          {ROOT_CAUSE_OPTIONS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      {/* Category */}
      <div>
        <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
          <Tag className="w-4 h-4 text-purple-500" />
          Category
        </label>
        <select
          name="category"
          value={form.category}
          onChange={handleChange}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select category</option>

              {ticket?.category &&
      CATEGORY_MAP[ticket.category]?.subs?.map((sub) => (
        <option key={sub.key} value={sub.key}>
          {sub.label}
        </option>
      ))}
{/* 

          {Object.values(CATEGORY_MAP.wrong_punch_or_info).map((category) => (
            <option key={category.label} value={category.label}>
              {category.label}
            </option>
          ))} */}
        </select>
      </div>

      {/* Description */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">
          Root Cause Description{" "}
          <span className="text-gray-400">(optional)</span>
        </label>
        <textarea
          name="rootCauseDescription"
          value={form.rootCauseDescription}
          onChange={handleChange}
          rows={3}
          placeholder="Add explanation if needed..."
          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>

      {/* Action */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <PlusCircle className="w-4 h-4" />
          {loading ? "Saving..." : "Add ACPT"}
        </button>
      </div>
      </>:null}

    </div>
  );
}

const Detail = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-gray-600">{label}</span>
    <span className="font-medium text-gray-900 text-right">{value}</span>
  </div>
);



function ComplaintCard({ ticket }) {

  console.log("ticketticket",ticket);

  if (ticket?.conductDetails) {
    const conduct = ticket.conductDetails;

    return (
      <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl shadow space-y-4">
        <h2 className="text-lg font-semibold text-yellow-800">
          Complaint Already Lodged
        </h2>

        <div className="text-sm space-y-2">
          <p><b>Category:</b> {conduct.category}</p>
          <p><b>Type:</b> {conduct.type}</p>
          <p>
            <b>Severity:</b>{" "}
            <span className={`px-2 py-1 rounded text-white text-xs ${
              conduct.severity === "critical"
                ? "bg-red-700"
                : conduct.severity === "high"
                ? "bg-red-500"
                : conduct.severity === "medium"
                ? "bg-orange-500"
                : "bg-gray-500"
            }`}>
              {conduct.severity}
            </span>
          </p>

          {ticket?.conductLogObjId?.deductionAmount > 0 && (
            <p className="text-red-600 font-medium">
              Salary Deduction: ₹{ticket.conductLogObjId.deductionAmount}
            </p>
          )}

          {ticket?.conductLogObjId?.description && (
            <div className="bg-white border p-3 rounded mt-2">
              <b>Description:</b>
              <p className="mt-1 text-gray-700">
                {ticket.conductLogObjId.description}
              </p>
            </div>
          )}

          <p className="text-xs text-gray-500">
            Issued By: {conduct?.actionBy?.userName}
          </p>
        </div>
      </div>
    );
  }}